// SPDX-License-Identifier: MIT
/**
 * Drawing primitives for interfaces: rounded boxes, soft shadows without per-frame blur, skeleton
 * bars, fitted labels and a small set of line icons drawn on a 24-unit grid.
 */
import { clamp } from '../../core/math.ts';
import type { Box } from '../path.ts';
import type { UiTokens } from './tokens.ts';
type Ctx = CanvasRenderingContext2D;
export type { Box };
/** Rounded-rectangle path (radius clamped to half the shorter side). */
export function rrect(ctx: Ctx, b: Box, r: number): void {
    ctx.beginPath();
    ctx.roundRect(b.x, b.y, Math.max(0, b.w), Math.max(0, b.h), clamp(r, 0, Math.min(b.w, b.h) / 2));
}
/** `b` shrunk by `d` on every side (a negative `d` grows it). */
export const inset = (b: Box, d: number): Box => ({ x: b.x + d, y: b.y + d, w: b.w - 2 * d, h: b.h - 2 * d });
/** Scale `b` about its centre. */
export const scaleBox = (b: Box, s: number): Box => ({
    x: b.x + (b.w * (1 - s)) / 2,
    y: b.y + (b.h * (1 - s)) / 2,
    w: b.w * s,
    h: b.h * s,
});
// --- Shadows ------------------------------------------------------------------------------------------
// A shadow is a blurred rounded rectangle rendered once per (radius, blur, colour) into a small sprite
// and stretched to any size with nine slices. Blur on a CPU rasteriser is the expensive part; this
// pays it once per process. The cache depends only on its key, so frames stay deterministic.
type Sprite = {
    canvas: OffscreenCanvas;
    pad: number;
    corner: number;
    size: number;
};
const sprites = new Map<string, Sprite>();
function sprite(r: number, blur: number, color: string): Sprite {
    const key = `${r}|${blur}|${color}`;
    let s = sprites.get(key);
    if (s)
        return s;
    const pad = Math.ceil(blur * 1.25) + 2;
    const corner = pad + Math.ceil(r) + 1;
    const size = corner * 2 + 2;
    const canvas = new OffscreenCanvas(size, size);
    const c = canvas.getContext('2d')!;
    // Draw the shape far off-canvas and keep only its shadow, so the sprite holds no fill.
    const off = size * 4;
    c.shadowColor = color;
    c.shadowBlur = blur;
    c.shadowOffsetX = off;
    c.fillStyle = color;
    c.beginPath();
    c.roundRect(pad - off, pad, size - 2 * pad, size - 2 * pad, r);
    c.fill();
    s = { canvas, pad, corner, size };
    sprites.set(key, s);
    return s;
}
/** Nine-slice draw of a shadow sprite around `b`, offset by `dy`. */
function drawShadow(ctx: Ctx, b: Box, r: number, blur: number, dy: number, color: string, alpha: number): void {
    if (alpha <= 0.002 || b.w <= 0 || b.h <= 0)
        return;
    const rr = Math.round(clamp(r, 0, Math.min(b.w, b.h) / 2));
    const { canvas, pad, corner: c, size } = sprite(rr, Math.round(blur), color);
    const x0 = b.x - pad;
    const y0 = b.y - pad + dy;
    const x1 = b.x + b.w + pad;
    const y1 = b.y + b.h + pad + dy;
    const mw = Math.max(0, x1 - x0 - 2 * c);
    const mh = Math.max(0, y1 - y0 - 2 * c);
    // Boxes smaller than two corners: shrink the corners rather than overlap them.
    const cw = Math.min(c, (x1 - x0) / 2);
    const ch = Math.min(c, (y1 - y0) / 2);
    const m = size - 2 * c; // the stretchable middle strip of the sprite (2 px)
    const prev = ctx.globalAlpha;
    ctx.globalAlpha = prev * alpha;
    const cols: Array<[
        sx: number,
        sw: number,
        dx: number,
        dw: number
    ]> = [
        [0, c, x0, cw],
        [c, m, x0 + cw, mw],
        [size - c, c, x1 - cw, cw],
    ];
    const rows: Array<[
        sy: number,
        sh: number,
        dy: number,
        dh: number
    ]> = [
        [0, c, y0, ch],
        [c, m, y0 + ch, mh],
        [size - c, c, y1 - ch, ch],
    ];
    for (const [sx, sw, dx, dw] of cols)
        for (const [sy, sh, dyy, dh] of rows)
            if (dw > 0 && dh > 0)
                ctx.drawImage(canvas, sx, sy, sw, sh, dx, dyy, dw, dh);
    ctx.globalAlpha = prev;
}
/**
 * Soft elevation under a box: a tight key shadow plus a wide ambient one. `level` 0–3 (fractions
 * blend), so a card can lift smoothly as it is picked up.
 */
export function elevation(ctx: Ctx, tk: UiTokens, b: Box, r: number, level: number): void {
    if (level <= 0)
        return;
    const l = clamp(level, 0, 3);
    const { color, strength } = tk.shadow;
    drawShadow(ctx, b, r, 6 + 6 * l, 2 + 3 * l, color, strength * 0.5 * Math.min(1, l));
    drawShadow(ctx, b, r, 20 + 22 * l, 6 + 10 * l, color, strength * (0.35 + 0.12 * l) * Math.min(1, l));
}
/** A filled panel with its hairline border (and, on dark grounds, a faint top highlight). */
export function panel(ctx: Ctx, tk: UiTokens, b: Box, { r = tk.radius.lg, fill = tk.surface, lift = 1, border = true, }: Partial<{
    r: number;
    fill: string;
    lift: number;
    border: boolean;
}> = {}): void {
    elevation(ctx, tk, b, r, lift);
    rrect(ctx, b, r);
    ctx.fillStyle = fill;
    ctx.fill();
    if (border) {
        ctx.lineWidth = 1.5;
        ctx.strokeStyle = tk.border;
        rrect(ctx, inset(b, 0.75), r - 0.75);
        ctx.stroke();
    }
    if (tk.dark) {
        // Light catches the top edge of a raised panel on a dark ground.
        ctx.save();
        rrect(ctx, b, r);
        ctx.clip();
        const g = ctx.createLinearGradient(0, b.y, 0, b.y + Math.min(b.h, 48));
        g.addColorStop(0, 'rgba(255,255,255,0.06)');
        g.addColorStop(1, 'rgba(255,255,255,0)');
        ctx.fillStyle = g;
        ctx.fillRect(b.x, b.y, b.w, Math.min(b.h, 48));
        ctx.restore();
    }
}
/** A placeholder content bar: what a row or a paragraph would hold, without inventing text. */
export function skeleton(ctx: Ctx, tk: UiTokens, x: number, y: number, w: number, h = 16, alpha = 1): void {
    if (alpha <= 0 || w <= 0)
        return;
    const prev = ctx.globalAlpha;
    ctx.globalAlpha = prev * alpha;
    rrect(ctx, { x, y, w, h }, h / 2);
    ctx.fillStyle = tk.skeleton;
    ctx.fill();
    ctx.globalAlpha = prev;
}
/**
 * Set `ctx.font` to the largest size ≤ `px` (and ≥ `minPx`) at which `text` fits `maxW`, and
 * return it. Labels never go below the caption size, so they stay readable on video.
 */
export function fitLabel(ctx: Ctx, tk: UiTokens, text: string, maxW: number, px: number, weight = 500, minPx: number = tk.type.caption): number {
    let size = px;
    ctx.font = tk.font(size, weight);
    while (size > minPx && ctx.measureText(text).width > maxW) {
        size = Math.max(minPx, size - 1);
        ctx.font = tk.font(size, weight);
    }
    return size;
}
// --- Icons --------------------------------------------------------------------------------------------
export type IconName = 'search' | 'check' | 'plus' | 'close' | 'arrow' | 'chevron' | 'bell' | 'user' | 'upload' | 'link' | 'sliders' | 'play' | 'chart';
/** Paths on a 24 × 24 grid; strokes are 2 units wide with round ends. */
const ICONS: Record<IconName, (c: Ctx) => void> = {
    search: (c) => {
        c.moveTo(17, 10.5);
        c.arc(10.5, 10.5, 6.5, 0, Math.PI * 2);
        c.moveTo(15.5, 15.5);
        c.lineTo(20.5, 20.5);
    },
    check: (c) => {
        c.moveTo(4.5, 12.5);
        c.lineTo(9.5, 17.5);
        c.lineTo(19.5, 6.5);
    },
    plus: (c) => {
        c.moveTo(12, 5);
        c.lineTo(12, 19);
        c.moveTo(5, 12);
        c.lineTo(19, 12);
    },
    close: (c) => {
        c.moveTo(6, 6);
        c.lineTo(18, 18);
        c.moveTo(18, 6);
        c.lineTo(6, 18);
    },
    arrow: (c) => {
        c.moveTo(4.5, 12);
        c.lineTo(19, 12);
        c.moveTo(13, 6);
        c.lineTo(19, 12);
        c.lineTo(13, 18);
    },
    chevron: (c) => {
        c.moveTo(6, 9);
        c.lineTo(12, 15);
        c.lineTo(18, 9);
    },
    bell: (c) => {
        c.moveTo(6, 16.5);
        c.lineTo(6, 10.5);
        c.bezierCurveTo(6, 7, 8.7, 4.5, 12, 4.5);
        c.bezierCurveTo(15.3, 4.5, 18, 7, 18, 10.5);
        c.lineTo(18, 16.5);
        c.lineTo(19.5, 17.5);
        c.lineTo(4.5, 17.5);
        c.closePath();
        c.moveTo(10, 20.5);
        c.lineTo(14, 20.5);
    },
    user: (c) => {
        c.moveTo(16, 8.5);
        c.arc(12, 8.5, 4, 0, Math.PI * 2);
        c.moveTo(4.5, 20.5);
        c.bezierCurveTo(5.5, 16.5, 8.5, 14.5, 12, 14.5);
        c.bezierCurveTo(15.5, 14.5, 18.5, 16.5, 19.5, 20.5);
    },
    upload: (c) => {
        c.moveTo(12, 16);
        c.lineTo(12, 4.5);
        c.moveTo(7, 9.5);
        c.lineTo(12, 4.5);
        c.lineTo(17, 9.5);
        c.moveTo(4.5, 15);
        c.lineTo(4.5, 19.5);
        c.lineTo(19.5, 19.5);
        c.lineTo(19.5, 15);
    },
    link: (c) => {
        c.moveTo(10, 14);
        c.lineTo(14, 10);
        c.moveTo(11, 7);
        c.lineTo(13, 5);
        c.bezierCurveTo(15, 3, 18.5, 3, 20, 4.5);
        c.bezierCurveTo(21.5, 6, 21, 9, 19, 11);
        c.lineTo(17, 13);
        c.moveTo(13, 17);
        c.lineTo(11, 19);
        c.bezierCurveTo(9, 21, 5.5, 21, 4, 19.5);
        c.bezierCurveTo(2.5, 18, 3, 15, 5, 13);
        c.lineTo(7, 11);
    },
    sliders: (c) => {
        for (const [y, k] of [
            [7, 15],
            [17, 9],
        ] as const) {
            c.moveTo(4, y);
            c.lineTo(20, y);
            c.moveTo(k + 2.5, y);
            c.arc(k, y, 2.5, 0, Math.PI * 2);
        }
    },
    play: (c) => {
        c.moveTo(8, 5);
        c.lineTo(19, 12);
        c.lineTo(8, 19);
        c.closePath();
    },
    chart: (c) => {
        c.moveTo(5, 19.5);
        c.lineTo(5, 13);
        c.moveTo(10, 19.5);
        c.lineTo(10, 8);
        c.moveTo(15, 19.5);
        c.lineTo(15, 11);
        c.moveTo(20, 19.5);
        c.lineTo(20, 4.5);
    },
};
/**
 * Draw a line icon with its top-left at (x, y), `size` px square. `draw` 0–1 strokes it on (the
 * check mark ticking in); other icons simply fade with it.
 */
export function icon(ctx: Ctx, name: IconName, x: number, y: number, size: number, color: string, draw = 1): void {
    if (draw <= 0)
        return;
    ctx.save();
    ctx.translate(x, y);
    ctx.scale(size / 24, size / 24);
    ctx.lineWidth = 2;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.strokeStyle = color;
    ctx.beginPath();
    ICONS[name](ctx);
    if (name === 'check' && draw < 1) {
        // The check is two strokes of known length (7.07 + 14.87).
        const len = 21.94;
        ctx.setLineDash([len * draw, len]);
    }
    else if (draw < 1)
        ctx.globalAlpha *= draw;
    ctx.stroke();
    ctx.restore();
}
